import { useTheme } from '@shopify/restyle';
import {
  IconCalendarCheck,
  IconCalendarPlus,
  IconChevronRight,
  IconCircleCheck,
  IconClock,
  IconPackageExport,
  IconPackageImport,
  IconRotateClockwise,
} from '@tabler/icons-react-native';
import { userInfoAtom } from 'app/atoms';
import { ListingThumbnail } from 'app/components/listing-thumbnail';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { useNavigation } from 'app/hooks/navigation';
import { addLoanMilestoneToCalendar, type LoanCalendarMilestone } from 'app/lib/calendar';
import { navigateToLoan } from 'app/screens/root-stack-helper';
import { ModalCalendarSetting } from 'app/screens/profile/my-preferences/calendar/modal';
import type { Theme } from 'app/shopify';
import { ListingSubtype, TransactionMode } from 'app/types/listing';
import { getListingTransactionMode } from 'app/lib/listing';
import type { Loan } from 'app/types/loan';
import { LoanType } from 'app/types/loan';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { isMobileNative } from 'app/utils/web-helpers';
import { useAtomValue } from 'jotai';
import type { TFunction } from 'i18next';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, FlatList, type LayoutChangeEvent, Pressable, StyleSheet, View } from 'react-native';
import Animated, { FadeInRight } from 'react-native-reanimated';
import Toast from 'react-native-toast-message';

import {
  buildUpcomingActivityEvents,
  type UpcomingActivityEvent,
  type UpcomingActivityKind,
  type UpcomingActivityPriority,
} from './upcoming-activity-utils';

const MOBILE_CARD_WIDTH = 300;
const DESKTOP_CARD_WIDTH = 300;
const DESKTOP_SINGLE_CARD_MAX_WIDTH = 350;
const CARD_GAP = 12;
const MULTIPLE_CARDS_VISIBLE_RATIO = 0.86;

const ACTIVITY_WINDOW_FILTERS = [
  { days: null, key: 'all' },
  { days: 3, key: 'threeDays' },
  { days: 7, key: 'oneWeek' },
  { days: 30, key: 'oneMonth' },
  { days: 90, key: 'threeMonths' },
  { days: 180, key: 'sixMonths' },
] as const;

type ActivityWindowFilterKey = (typeof ACTIVITY_WINDOW_FILTERS)[number]['key'];
type CalendarState = 'added' | 'adding' | 'idle';

function getCalendarActionKey(loan: Loan, milestone: LoanCalendarMilestone) {
  return `${loan.id}:${milestone as string}`;
}

function getEventColors(priority: UpcomingActivityPriority, theme: Theme) {
  switch (priority) {
    case 'overdue':
      return {
        accent: theme.colors['surface.error.default'],
        subtle: theme.colors['surface.error.subtle'],
      };
    case 'attention':
      return {
        accent: theme.colors['surface.warning.default'],
        subtle: theme.colors['surface.warning.subtle'],
      };
    case 'today':
      return {
        accent: theme.colors['surface.brand.default'],
        subtle: theme.colors['surface.brand.subtle'],
      };
    case 'soon':
      return {
        accent: theme.colors['surface.info.default'],
        subtle: theme.colors['surface.info.subtle'],
      };
  }
}

function EventIcon({ color, kind }: { color: string; kind: UpcomingActivityKind }) {
  const props = { color, size: 14, strokeWidth: ICON_STROKE_WIDTH };
  switch (kind) {
    case 'handOff':
      return <IconPackageExport {...props} />;
    case 'pickUp':
      return <IconPackageImport {...props} />;
    case 'returnItem':
      return <IconRotateClockwise {...props} />;
    case 'expectReturn':
      return <IconClock {...props} />;
    case 'confirmReturn':
    case 'confirmSale':
    case 'completePurchase':
    case 'completeSale':
      return <IconCircleCheck {...props} />;
  }
}

function getOtherUser(event: UpcomingActivityEvent) {
  return event.loanType === LoanType.Lending ? event.loan.borrower : event.loan.lender;
}

function getCalendarMilestone(event: UpcomingActivityEvent): LoanCalendarMilestone | null {
  if (
    event.daysFromToday < 0 ||
    event.kind === 'confirmReturn' ||
    event.kind === 'confirmSale' ||
    event.kind === 'completePurchase' ||
    event.kind === 'completeSale'
  )
    return null;
  if (event.kind === 'handOff' || event.kind === 'pickUp') return 'start';
  return event.loan.listing.subtype === ListingSubtype.object ? 'end' : null;
}

function getActivityRolePrefix(transactionMode: TransactionMode) {
  if (transactionMode === TransactionMode.sell) return 'saleRoles';
  if (transactionMode === TransactionMode.give) return 'donationRoles';
  return 'roles';
}

function getOtherUserName(event: UpcomingActivityEvent, t: TFunction<'landing-page'>) {
  const otherUser = getOtherUser(event);
  return otherUser.profile?.firstName || otherUser.profile?.lastName || t('upcomingActivity.deletedUser');
}

function getRelativeDate(event: UpcomingActivityEvent, t: TFunction<'landing-page'>) {
  if (event.kind === 'confirmReturn' || event.kind === 'confirmSale') {
    return t('upcomingActivity.relative.actionRequired');
  }
  if (event.daysFromToday < 0) {
    return t('upcomingActivity.relative.overdue', { count: Math.abs(event.daysFromToday) });
  }
  if (event.daysFromToday === 0) return t('upcomingActivity.relative.today');
  if (event.daysFromToday === 1) return t('upcomingActivity.relative.tomorrow');
  return t('upcomingActivity.relative.inDays', { count: event.daysFromToday });
}

function CalendarActionIcon({ state, theme }: { state: CalendarState; theme: Theme }) {
  if (state === 'adding') {
    return <ActivityIndicator color={theme.colors['icon.interactive']} size="small" />;
  }
  if (state === 'added') {
    return <IconCalendarCheck color={theme.colors['icon.success']} size={23} strokeWidth={ICON_STROKE_WIDTH} />;
  }
  return <IconCalendarPlus color={theme.colors['icon.interactive']} size={23} strokeWidth={ICON_STROKE_WIDTH} />;
}

function CalendarAction({
  calendarState,
  event,
  onPress,
}: {
  calendarState: CalendarState;
  event: UpcomingActivityEvent;
  onPress?: () => void;
}) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('landing-page');

  if (!onPress) return null;

  const isAdded = calendarState === 'added';
  const isIdle = calendarState === 'idle';
  return (
    <Pressable
      accessibilityLabel={t(isAdded ? 'upcomingActivity.calendar.addedLabel' : 'upcomingActivity.calendar.addLabel')}
      accessibilityRole="button"
      accessibilityState={{ busy: calendarState === 'adding', disabled: !isIdle }}
      disabled={!isIdle}
      hitSlop={8}
      onPress={onPress}
      style={({ pressed }) => [
        styles.calendarButton,
        {
          backgroundColor: isAdded ? theme.colors['surface.success.subtle'] : theme.colors['surface.brand.subtle'],
          opacity: pressed ? 0.65 : 1,
        },
      ]}
      testID={`upcoming-activity-calendar-${event.loan.id}`}
    >
      <CalendarActionIcon state={calendarState} theme={theme} />
    </Pressable>
  );
}

function UpcomingActivityCard({
  calendarState,
  cardWidth,
  event,
  index,
  onAddToCalendar,
}: {
  calendarState: CalendarState;
  cardWidth: number;
  event: UpcomingActivityEvent;
  index: number;
  onAddToCalendar?: () => void;
}) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('landing-page');
  const navigation = useNavigation();
  const colors = getEventColors(event.priority, theme);
  const transactionMode = getListingTransactionMode(event.loan.listing);
  const rolePrefix = getActivityRolePrefix(transactionMode);
  const otherUserName = getOtherUserName(event, t);
  const relativeDate = getRelativeDate(event, t);

  return (
    <Animated.View entering={FadeInRight.delay(index * 45).duration(240)} style={{ width: cardWidth }}>
      <View
        style={[
          styles.eventCard,
          {
            backgroundColor: theme.colors['surface.default'],
            borderColor: theme.colors['surface.inverse.tertiary-neutral'],
          },
        ]}
      >
        <Pressable
          accessibilityHint={t('upcomingActivity.openDetails')}
          accessibilityLabel={`${t(`upcomingActivity.actions.${event.kind}`, { name: otherUserName })}, ${event.loan.listing.item.name}, ${relativeDate}`}
          accessibilityRole="button"
          onPress={() => navigateToLoan(navigation, event.loan.id, event.loan.listing.id)}
          style={({ pressed }) => [
            styles.mainCardAction,
            {
              backgroundColor: pressed ? theme.colors['surface.primary-neutral'] : 'transparent',
              opacity: pressed ? 0.82 : 1,
            },
          ]}
          testID={`upcoming-activity-${event.loan.id}`}
        >
          <Row
            alignItems="center"
            gap="spacing.general.x2"
            paddingRight={onAddToCalendar ? 'spacing.general.x14' : undefined}
          >
            <Row
              alignItems="center"
              gap="spacing.general.x1"
              style={[styles.urgencyChip, { backgroundColor: colors.subtle }]}
            >
              <EventIcon color={colors.accent} kind={event.kind} />
              <Text
                color={event.priority === 'overdue' ? 'text.error' : 'text.primary'}
                fontSize={11}
                fontWeight="700"
                numberOfLines={1}
              >
                {relativeDate}
              </Text>
              <View style={[styles.chipDivider, { backgroundColor: theme.colors['text.placeholder'] }]} />
              <Text color="text.secondary" fontSize={10} fontWeight="700" numberOfLines={1}>
                {t(`upcomingActivity.${rolePrefix}.${event.loanType}`)}
              </Text>
            </Row>
          </Row>

          <Row alignItems="center" gap="spacing.general.x3">
            <ListingThumbnail hideCorpoBadge size={54} {...event.loan.listing} />

            <Column flex={1} minWidth={0} gap="spacing.general.x1">
              <Text fontSize={15} fontWeight="600" lineHeight={19} numberOfLines={2}>
                {t(`upcomingActivity.actions.${event.kind}`, { name: otherUserName })}
              </Text>

              <Text color="text.secondary" fontSize={12} numberOfLines={1}>
                {event.loan.listing.item.name}
              </Text>
            </Column>

            <IconChevronRight color={theme.colors['icon.tertiary']} size={17} strokeWidth={ICON_STROKE_WIDTH} />
          </Row>
        </Pressable>

        <CalendarAction calendarState={calendarState} event={event} onPress={onAddToCalendar} />
      </View>
    </Animated.View>
  );
}

function ActivitySkeleton({ cardWidth }: { cardWidth: number }) {
  const theme = useTheme<Theme>();
  return (
    <Row gap="spacing.general.x3">
      {['first', 'second'].map((key) => (
        <Column
          key={key}
          gap="spacing.general.x3"
          style={[
            styles.eventCard,
            styles.skeletonCard,
            { borderColor: theme.colors['surface.inverse.tertiary-neutral'], width: cardWidth },
          ]}
        >
          <View style={[styles.skeletonLineShort, { backgroundColor: theme.colors['surface.disabled'] }]} />
          <Row alignItems="center" gap="spacing.general.x3">
            <View style={[styles.skeletonThumbnail, { backgroundColor: theme.colors['surface.disabled'] }]} />
            <Column flex={1} gap="spacing.general.x2">
              <View style={[styles.skeletonLine, { backgroundColor: theme.colors['surface.disabled'] }]} />
              <View style={[styles.skeletonLineShort, { backgroundColor: theme.colors['surface.disabled'] }]} />
            </Column>
          </Row>
        </Column>
      ))}
    </Row>
  );
}

function ActivityWindowFilters({
  onChange,
  value,
  extraPadding = 0,
}: {
  onChange: (value: ActivityWindowFilterKey) => void;
  value: ActivityWindowFilterKey;
  extraPadding?: number;
}) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('landing-page');

  return (
    <FlatList
      contentContainerStyle={[styles.filterContent, { paddingHorizontal: extraPadding }]}
      data={ACTIVITY_WINDOW_FILTERS}
      horizontal
      keyExtractor={(filter) => filter.key}
      renderItem={({ item: filter }) => {
        const selected = filter.key === value;
        return (
          <Pressable
            accessibilityRole="button"
            accessibilityState={{ selected }}
            onPress={() => onChange(filter.key)}
            style={({ pressed }) => [
              styles.filterChip,
              {
                backgroundColor: selected ? theme.colors['surface.brand.subtle'] : theme.colors['surface.default'],
                borderColor: selected ? theme.colors['border.interactive'] : theme.colors['border.secondary'],
                opacity: pressed ? 0.7 : 1,
              },
            ]}
            testID={`upcoming-activity-filter-${filter.key}`}
          >
            <Text color={selected ? 'text.emphasized' : 'text.secondary'} fontSize={11} fontWeight="600">
              {t(`upcomingActivity.filters.${filter.key}`)}
            </Text>
          </Pressable>
        );
      }}
      showsHorizontalScrollIndicator={false}
      style={styles.filterViewport}
    />
  );
}

type UpcomingActivityTimeWindow = 'all' | 'nextThreeDays';
type UpcomingActivityVariant = 'large' | 'small';

function getWindowDays({
  selectedWindow,
  showWindowFilters,
  timeWindow,
}: {
  selectedWindow: (typeof ACTIVITY_WINDOW_FILTERS)[number];
  showWindowFilters: boolean;
  timeWindow: UpcomingActivityTimeWindow;
}) {
  if (showWindowFilters) return selectedWindow.days;
  return timeWindow === 'all' ? null : undefined;
}

function getUpcomingEvents({
  borrowings,
  excludedLoanIds,
  lendings,
  windowDays,
}: {
  borrowings: Loan[];
  excludedLoanIds: Loan['id'][];
  lendings: Loan[];
  windowDays: number | null | undefined;
}) {
  const excluded = new Set(excludedLoanIds);
  return buildUpcomingActivityEvents({ borrowings, lendings, windowDays }).filter(
    (event) => !excluded.has(event.loan.id),
  );
}

function getActivityCardWidth({
  eventCount,
  mobileHorizontalPadding,
  railWidth,
  variant,
}: {
  eventCount: number;
  mobileHorizontalPadding: number;
  railWidth: number;
  variant: UpcomingActivityVariant;
}) {
  const defaultWidth = variant === 'large' ? DESKTOP_CARD_WIDTH : MOBILE_CARD_WIDTH;
  if (railWidth <= 0) return defaultWidth;
  if (eventCount === 1) {
    return variant === 'large'
      ? Math.min(DESKTOP_SINGLE_CARD_MAX_WIDTH, railWidth)
      : railWidth - mobileHorizontalPadding;
  }
  const maximumWidth = variant === 'large' ? DESKTOP_CARD_WIDTH : MOBILE_CARD_WIDTH;
  return Math.min(maximumWidth, railWidth * MULTIPLE_CARDS_VISIBLE_RATIO);
}

function getWindowSubtitle({
  selectedWindow,
  showWindowFilters,
  t,
  timeWindow,
}: {
  selectedWindow: (typeof ACTIVITY_WINDOW_FILTERS)[number];
  showWindowFilters: boolean;
  t: TFunction<'landing-page'>;
  timeWindow: UpcomingActivityTimeWindow;
}) {
  if (!showWindowFilters) {
    return t(timeWindow === 'all' ? 'upcomingActivity.allWindow' : 'upcomingActivity.window');
  }
  if (selectedWindow.days === null) return t('upcomingActivity.allWindow');
  return t('upcomingActivity.filteredWindow', {
    period: t(`upcomingActivity.filters.${selectedWindow.key}`),
  });
}

function getCalendarState({
  addedCalendarActionKeys,
  addingCalendarActionKey,
  calendarActionKey,
}: {
  addedCalendarActionKeys: Set<string>;
  addingCalendarActionKey: string | null;
  calendarActionKey: string | null;
}): CalendarState {
  if (calendarActionKey && addedCalendarActionKeys.has(calendarActionKey)) return 'added';
  if (calendarActionKey === addingCalendarActionKey) return 'adding';
  return 'idle';
}

function useUpcomingActivityCalendar() {
  const { t } = useTranslation('landing-page');
  const { t: tCalendar } = useTranslation('one-channel');
  const user = useAtomValue(userInfoAtom);
  const [calendarSettingsOpen, setCalendarSettingsOpen] = useState(false);
  const [addingCalendarActionKey, setAddingCalendarActionKey] = useState<string | null>(null);
  const [addedCalendarActionKeys, setAddedCalendarActionKeys] = useState<Set<string>>(() => new Set());

  const addToCalendar = async (loan: Loan, milestone: LoanCalendarMilestone) => {
    if (!user?.id) return;
    const calendarActionKey = getCalendarActionKey(loan, milestone);
    setAddingCalendarActionKey(calendarActionKey);
    const result = await addLoanMilestoneToCalendar(tCalendar, user.id, loan, milestone);
    setAddingCalendarActionKey(null);

    if (result === 'permission-denied') {
      setCalendarSettingsOpen(true);
      return;
    }
    if (result !== 'success') {
      Toast.show({
        bottomOffset: 100,
        position: 'bottom',
        text1: tCalendar('genericError', { ns: 'common' }),
        type: 'error',
        visibilityTime: 3000,
      });
      return;
    }

    setAddedCalendarActionKeys((current) => new Set(current).add(calendarActionKey));
    Toast.show({
      bottomOffset: 100,
      position: 'bottom',
      text1: t('upcomingActivity.calendar.added', { item: loan.listing.item.name }),
      type: 'success',
      visibilityTime: 2500,
    });
  };

  return {
    addedCalendarActionKeys,
    addingCalendarActionKey,
    addToCalendar,
    calendarSettingsOpen,
    setCalendarSettingsOpen,
    userId: user?.id,
  };
}

function UpcomingActivityHeader({
  eventCount,
  extraPadding,
  isLoading,
  onViewAll,
  timeWindow,
  windowSubtitle,
}: {
  eventCount: number;
  extraPadding: number;
  isLoading: boolean;
  onViewAll?: () => void;
  timeWindow: UpcomingActivityTimeWindow;
  windowSubtitle: string;
}) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('landing-page');
  const showCount = !isLoading && eventCount > 1;

  return (
    <Row alignItems="flex-start" gap="spacing.general.x3" style={{ paddingHorizontal: extraPadding }}>
      <Column flex={1} minWidth={0} gap="spacing.general.x0,5">
        <Row alignItems="center" gap="spacing.general.x2">
          <Text fontWeight="700" variant="bodyLarge">
            {t(timeWindow === 'all' ? 'upcomingActivity.allTitle' : 'upcomingActivity.title')}
          </Text>
          {showCount ? (
            <Row
              alignItems="center"
              justifyContent="center"
              style={[styles.countBadge, { backgroundColor: theme.colors['surface.primary-neutral'] }]}
            >
              <Text color="text.secondary" fontSize={11} fontWeight="700">
                {eventCount}
              </Text>
            </Row>
          ) : null}
        </Row>
        <Text color="text.secondary" variant="bodyCaption">
          {windowSubtitle}
        </Text>
      </Column>
      {onViewAll ? (
        <Pressable
          accessibilityRole="button"
          onPress={onViewAll}
          style={({ pressed }) => ({ opacity: pressed ? 0.55 : 1, paddingTop: 2 })}
        >
          <Row alignItems="center" gap="spacing.general.x1">
            <Text color="text.brand" fontSize={14} fontWeight="700">
              {t('upcomingActivity.viewAll')}
            </Text>
            <IconChevronRight color={theme.colors['text.brand']} size={18} strokeWidth={ICON_STROKE_WIDTH} />
          </Row>
        </Pressable>
      ) : null}
    </Row>
  );
}

function UpcomingActivityListItem({
  addedCalendarActionKeys,
  addingCalendarActionKey,
  cardWidth,
  event,
  index,
  onAddToCalendar,
  showCalendarAction,
}: {
  addedCalendarActionKeys: Set<string>;
  addingCalendarActionKey: string | null;
  cardWidth: number;
  event: UpcomingActivityEvent;
  index: number;
  onAddToCalendar: (loan: Loan, milestone: LoanCalendarMilestone) => Promise<void>;
  showCalendarAction: boolean;
}) {
  const milestone = getCalendarMilestone(event);
  const calendarActionKey = milestone ? getCalendarActionKey(event.loan, milestone) : null;
  const calendarState = getCalendarState({
    addedCalendarActionKeys,
    addingCalendarActionKey,
    calendarActionKey,
  });
  const handleAddToCalendar = () => {
    if (milestone) void onAddToCalendar(event.loan, milestone);
  };

  return (
    <UpcomingActivityCard
      calendarState={calendarState}
      cardWidth={cardWidth}
      event={event}
      index={index}
      onAddToCalendar={showCalendarAction && milestone ? handleAddToCalendar : undefined}
    />
  );
}

function UpcomingActivityList({
  addedCalendarActionKeys,
  addingCalendarActionKey,
  cardWidth,
  events,
  extraPadding,
  onAddToCalendar,
  showCalendarAction,
}: {
  addedCalendarActionKeys: Set<string>;
  addingCalendarActionKey: string | null;
  cardWidth: number;
  events: UpcomingActivityEvent[];
  extraPadding: number;
  onAddToCalendar: (loan: Loan, milestone: LoanCalendarMilestone) => Promise<void>;
  showCalendarAction: boolean;
}) {
  const itemCardWidth = events.length > 1 ? cardWidth : cardWidth - extraPadding;
  return (
    <FlatList
      accessibilityRole="list"
      contentContainerStyle={[styles.railContent, { paddingHorizontal: extraPadding }]}
      data={events}
      decelerationRate="fast"
      horizontal
      keyExtractor={(event) => event.loan.id}
      nestedScrollEnabled
      renderItem={({ item: event, index }) => (
        <UpcomingActivityListItem
          addedCalendarActionKeys={addedCalendarActionKeys}
          addingCalendarActionKey={addingCalendarActionKey}
          cardWidth={itemCardWidth}
          event={event}
          index={index}
          onAddToCalendar={onAddToCalendar}
          showCalendarAction={showCalendarAction}
        />
      )}
      scrollEnabled={events.length > 1}
      showsHorizontalScrollIndicator={false}
      snapToAlignment="start"
      snapToInterval={cardWidth + CARD_GAP}
    />
  );
}

function UpcomingActivityRail({
  addedCalendarActionKeys,
  addingCalendarActionKey,
  cardWidth,
  events,
  extraPadding,
  isLoading,
  onAddToCalendar,
  onLayout,
  showCalendarAction,
}: {
  addedCalendarActionKeys: Set<string>;
  addingCalendarActionKey: string | null;
  cardWidth: number;
  events: UpcomingActivityEvent[];
  extraPadding: number;
  isLoading: boolean;
  onAddToCalendar: (loan: Loan, milestone: LoanCalendarMilestone) => Promise<void>;
  onLayout: (event: LayoutChangeEvent) => void;
  showCalendarAction: boolean;
}) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('landing-page');

  if (isLoading) {
    return (
      <View onLayout={onLayout} style={styles.railViewport}>
        <ActivitySkeleton cardWidth={cardWidth} />
      </View>
    );
  }
  if (events.length === 0) {
    return (
      <Row
        alignItems="center"
        backgroundColor="surface.primary-neutral"
        borderRadius="radius.lg"
        gap="spacing.general.x2"
        padding="spacing.general.x3"
      >
        <IconCircleCheck color={theme.colors['icon.success']} size={18} strokeWidth={ICON_STROKE_WIDTH} />
        <Text color="text.secondary" fontSize={13}>
          {t('upcomingActivity.filters.empty')}
        </Text>
      </Row>
    );
  }
  return (
    <View onLayout={onLayout} style={styles.railViewport}>
      <UpcomingActivityList
        addedCalendarActionKeys={addedCalendarActionKeys}
        addingCalendarActionKey={addingCalendarActionKey}
        cardWidth={cardWidth}
        events={events}
        extraPadding={extraPadding}
        onAddToCalendar={onAddToCalendar}
        showCalendarAction={showCalendarAction}
      />
    </View>
  );
}

export function UpcomingActivity({
  borrowings,
  excludedLoanIds = [],
  isLoading,
  lendings,
  onViewAll,
  showWindowFilters = false,
  timeWindow = 'nextThreeDays',
  variant,
  extraPadding = 0,
}: {
  borrowings: Loan[];
  excludedLoanIds?: Loan['id'][];
  isLoading: boolean;
  lendings: Loan[];
  onViewAll?: () => void;
  showWindowFilters?: boolean;
  timeWindow?: UpcomingActivityTimeWindow;
  variant: UpcomingActivityVariant;
  extraPadding?: number;
}) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('landing-page');
  const calendar = useUpcomingActivityCalendar();
  const [selectedWindowFilter, setSelectedWindowFilter] = useState<ActivityWindowFilterKey>('threeDays');
  const selectedWindow =
    ACTIVITY_WINDOW_FILTERS.find((filter) => filter.key === selectedWindowFilter) ?? ACTIVITY_WINDOW_FILTERS[0];
  const windowDays = getWindowDays({ selectedWindow, showWindowFilters, timeWindow });
  const events = getUpcomingEvents({ borrowings, excludedLoanIds, lendings, windowDays });
  const [railWidth, setRailWidth] = useState(0);
  const cardWidth = getActivityCardWidth({
    eventCount: events.length,
    mobileHorizontalPadding: theme.spacing['spacing.general.x4'],
    railWidth,
    variant,
  });
  const handleRailLayout = (event: LayoutChangeEvent) => {
    setRailWidth(event.nativeEvent.layout.width);
  };

  if (!isLoading && events.length === 0 && !showWindowFilters) return null;

  const windowSubtitle = getWindowSubtitle({ selectedWindow, showWindowFilters, t, timeWindow });

  return (
    <>
      <Column
        gap="spacing.general.x3"
        style={[styles.container, { marginHorizontal: -extraPadding }]}
        testID="upcoming-activity"
      >
        <UpcomingActivityHeader
          eventCount={events.length}
          extraPadding={extraPadding}
          isLoading={isLoading}
          onViewAll={onViewAll}
          timeWindow={timeWindow}
          windowSubtitle={windowSubtitle}
        />

        {showWindowFilters ? (
          <ActivityWindowFilters
            extraPadding={extraPadding}
            onChange={setSelectedWindowFilter}
            value={selectedWindowFilter}
          />
        ) : null}

        <UpcomingActivityRail
          addedCalendarActionKeys={calendar.addedCalendarActionKeys}
          addingCalendarActionKey={calendar.addingCalendarActionKey}
          cardWidth={cardWidth}
          events={events}
          extraPadding={extraPadding}
          isLoading={isLoading}
          onAddToCalendar={calendar.addToCalendar}
          onLayout={handleRailLayout}
          showCalendarAction={Boolean(isMobileNative && calendar.userId)}
        />
      </Column>
      <ModalCalendarSetting modalState={[calendar.calendarSettingsOpen, calendar.setCalendarSettingsOpen]} />
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    overflow: 'hidden',
  },
  calendarButton: {
    alignItems: 'center',
    borderRadius: 22,
    height: 44,
    justifyContent: 'center',
    position: 'absolute',
    right: 10,
    top: 9,
    width: 44,
    zIndex: 2,
  },
  chipDivider: {
    borderRadius: 2,
    height: 3,
    width: 3,
  },
  countBadge: {
    borderRadius: 10,
    height: 20,
    minWidth: 20,
    paddingHorizontal: 6,
  },
  eventCard: {
    borderRadius: 16,
    borderWidth: 1,
    height: 112,
    overflow: 'hidden',
  },
  filterChip: {
    borderRadius: 14,
    borderWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  filterContent: {
    gap: 8,
    paddingRight: 2,
  },
  filterViewport: {
    width: '100%',
  },
  mainCardAction: {
    flex: 1,
    gap: 12,
    padding: 12,
  },
  railContent: {
    gap: CARD_GAP,
  },
  railViewport: {
    overflow: 'hidden',
    width: '100%',
  },
  skeletonCard: {
    gap: 12,
    padding: 12,
  },
  skeletonLine: {
    borderRadius: 5,
    height: 10,
    width: '68%',
  },
  skeletonLineShort: {
    borderRadius: 5,
    height: 8,
    width: '34%',
  },
  skeletonThumbnail: {
    borderRadius: 12,
    height: 54,
    width: 54,
  },
  urgencyChip: {
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 5,
  },
});
